import React, { useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Box, Button, Typography, Paper, TextField, Divider, IconButton } from '@mui/material';
import FeedbackService from 'services/FeedbackService';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SentimentVerySatisfiedIcon from '@mui/icons-material/SentimentVerySatisfied';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfied';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';

export default function PublicFeedbackForm() {
  const { shopId: pathShopId } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const urlToken = searchParams.get('token');
  
  const token = urlToken;
  const shopId = pathShopId;

  const [customerName, setCustomerName] = useState('');
  const [foodRating, setFoodRating] = useState(5); // Food (emojis: 1 to 5)
  const [serviceRating, setServiceRating] = useState(5); // Service (numbers)
  const [cleanliness, setCleanliness] = useState(5); // Cleanliness (numbers)
  const [comment, setComment] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const calculatedOverall = Math.round((foodRating + serviceRating + cleanliness) / 3);
    try {
      await FeedbackService.submitFeedback(shopId, { 
        customer_name: customerName, 
        overall: calculatedOverall, 
        food_rating: foodRating,
        service_rating: serviceRating,
        cleanliness,
        comment,
        token 
      });
      setSubmitted(true);
    } catch (error) {
      console.error("Failed to submit feedback:", error);
      alert("Failed to submit feedback. The backend endpoint might not be available yet.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh" bgcolor="#eeeeee">
        <Paper elevation={0} style={{ padding: '50px', textAlign: 'center', maxWidth: '450px', borderRadius: '12px' }}>
          <CheckCircleOutlineIcon style={{ fontSize: 80, color: '#000', margin: '0 auto 20px auto', display: 'block' }} />
          <Typography variant="h4" gutterBottom style={{ fontWeight: 600, color: '#000' }}>
            Thank You!
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Your feedback has been successfully submitted.
          </Typography>
        </Paper>
      </Box>
    );
  }

  const EmojiRatingGroup = ({ label, value, onChange }) => (
    <Box>
      <Typography variant="body2" style={{ fontWeight: 600, marginBottom: '12px', fontSize: '14px', color: '#000' }}>
        {label}
      </Typography>
      <Box display="flex" justifyContent="space-between" gap={1}>
        {[
          { val: 1, icon: <SentimentVeryDissatisfiedIcon fontSize="medium" />, text: 'Poor' },
          { val: 2, icon: <SentimentDissatisfiedIcon fontSize="medium" />, text: 'Fair' },
          { val: 3, icon: <SentimentSatisfiedIcon fontSize="medium" />, text: 'Good' },
          { val: 4, icon: <SentimentSatisfiedAltIcon fontSize="medium" />, text: 'Very Good' },
          { val: 5, icon: <SentimentVerySatisfiedIcon fontSize="medium" />, text: 'Excellent' }
        ].map(item => (
          <Box key={item.val} display="flex" flexDirection="column" alignItems="center">
            <IconButton 
              onClick={() => onChange(item.val)}
              style={{ 
                backgroundColor: value === item.val ? '#ebd9fc' : '#f5f5f5', 
                color: value === item.val ? '#000' : '#666',
                padding: '8px',
                transition: 'background-color 0.2s'
              }}
            >
              {item.icon}
            </IconButton>
            <Typography variant="caption" style={{ fontSize: '10px', marginTop: '4px', color: '#777' }}>{item.text}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );

  return (
    <Box 
      display="flex" 
      justifyContent="center" 
      alignItems="center" 
      minHeight="100vh" 
      py={4}
      px={2}
      bgcolor="#eeeeee"
      fontFamily="'Inter', sans-serif"
    >
      <Paper elevation={0} style={{ maxWidth: '500px', width: '100%', borderRadius: '12px', padding: '16px 24px 40px 24px' }}>
        <Typography variant="h5" style={{ fontWeight: 600, marginBottom: '24px' }}>
          Feedback
        </Typography>

        <form onSubmit={handleSubmit}>
          <Box display="flex" flexDirection="column" gap={3}>
            
            <Box>
              <Typography variant="body2" style={{ fontWeight: 600, marginBottom: '8px', fontSize: '13px' }}>
                Your Name 
              </Typography>
              <TextField
                placeholder="Enter your name"
                variant="outlined"
                fullWidth
                size="small"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                InputProps={{ 
                  style: { 
                    borderRadius: '8px',
                    fontSize: '14px',
                  } 
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#e0e0e0' },
                    '&:hover fieldset': { borderColor: '#b0b0b0' },
                    '&.Mui-focused fieldset': { borderColor: '#000' },
                  },
                }}
              />
            </Box>

            <Divider style={{ borderColor: '#f0f0f0' }} />

            <EmojiRatingGroup 
              label="How would you rate the taste of the food?" 
              value={foodRating} 
              onChange={setFoodRating} 
            />

            <Divider style={{ borderColor: '#f0f0f0' }} />

            <EmojiRatingGroup 
              label="How was the service you received?" 
              value={serviceRating} 
              onChange={setServiceRating} 
            />

            <Divider style={{ borderColor: '#f0f0f0' }} />

            <EmojiRatingGroup 
              label="How would you rate the cleanliness of our restaurant?" 
              value={cleanliness} 
              onChange={setCleanliness} 
            />

            <Divider style={{ borderColor: '#f0f0f0', margin: '8px 0' }} />

            <Box mt={1}>
              <Typography variant="body2" style={{ fontWeight: 600, marginBottom: '12px', fontSize: '14px' }}>
                Your feedback
              </Typography>
              <TextField
                placeholder="Please share any additional comments..."
                multiline
                rows={6}
                variant="outlined"
                fullWidth
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                InputProps={{ 
                  style: { 
                    borderRadius: '8px',
                    fontSize: '14px',
                  } 
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#e0e0e0' },
                    '&:hover fieldset': { borderColor: '#b0b0b0' },
                    '&.Mui-focused fieldset': { borderColor: '#000' },
                  },
                }}
              />
            </Box>

            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              style={{ 
                marginTop: '8px',
                backgroundColor: '#000',
                color: '#fff',
                padding: '14px 0', 
                fontSize: '14px', 
                fontWeight: 600, 
                textTransform: 'none',
                borderRadius: '8px',
                boxShadow: 'none'
              }}
            >
              {loading ? 'Sending...' : 'Send Feedback'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
}
